import noop from 'lodash/noop';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { push } from 'react-router-redux';
import { call, put, race, select, take } from 'redux-saga/effects';

import * as AppActions from '../../../../src/actions';
import * as AppConstants from '../../../../src/constants';
import * as AppSelectors from '../../../../src/selectors';
import { getInDomain } from '../../../domains/externalUtils';
import * as EntityUtils from '../../../lib/EntityUtils';
import * as MusicUtils from '../../../lib/MusicUtils';
import * as RouteUtils from '../../../lib/RouteUtils';
import { putAndWait } from '../../../sagas/TaskManager';
import { getStore } from '../../../store/StoreAccessor';
import * as Globals from '../../../tools/Globals';
import Logger from '../../../tools/Logger';

const log = Logger.child({
  component: 'mediator/useCases/handlePremiumAccess/helpers',
});

function onUpsellButtonClick() {
  log.info('onUpsellButtonClick');
  const { dispatch } = getStore();
  dispatch(getInDomain('analytics.actions.logClickedLTUXPAUpsellCoachmark')());
  dispatch(AppActions.Modal.closeModal());
  dispatch(push(RouteUtils.getUpgradePath()));
}

export function* openUpsellModal() {
  const isTrialEligible = yield select(AppSelectors.Payments.isTrialEligible);
  log.info({ isTrialEligible }, 'openUpsellModal');
  const { messages } = AppConstants.Modal;
  let options = {
    timeout: false,
  };

  if (isTrialEligible) {
    options = {
      ...options,
      bodyMessage: <FormattedMessage {...messages.premiumAccessEndTrialEligibleHeader} />,
      buttonMessage: <FormattedMessage {...messages.premiumAccessEndTrialEligibleButton} />,
      buttonCallback: onUpsellButtonClick,
    };
  } else {
    options = {
      ...options,
      bodyMessage: <FormattedMessage {...messages.premiumAccessEndTrialIneligibleHeader} />,
      buttonMessage: <FormattedMessage {...messages.premiumAccessEndTrialIneligibleButton} />,
      buttonCallback: onUpsellButtonClick,
    };
  }

  yield put(AppActions.Modal.openModalCoachmarkWithButton(options));
  yield put(getInDomain('analytics.actions.logOpenedLTUXPAUpsellCoachmark', noop)());
}

export function* getEndPremiumAccessSearchData() {
  let forceReroute = true;
  let rerouteUrl;
  let isStartNewStation;
  const pathname = Globals.getDocumentLocationPathname();
  const isSearch = RouteUtils.isInSearchPath(pathname);
  log.info({ pathname, isSearch }, 'getEndPremiumAccessSearchData');
  if (isSearch) {
    rerouteUrl = yield select(RouteUtils.getNowPlayingPath);
    const currentPlaylistId = yield select(getInDomain('playback.utils.getCurrentPlaylist'));
    const isOnDemandId = MusicUtils.isOnDemandIdLike(currentPlaylistId);
    if (isOnDemandId) {
      forceReroute = false;
      const source = yield select(EntityUtils.getAnnotationsById, currentPlaylistId);
      rerouteUrl = source.get('shareableUrlPath');
    } else {
      isStartNewStation = true;
    }
  }
  return {
    forceReroute,
    rerouteUrl,
    isStartNewStation,
  };
}

export function* endPremiumAccessReroute() {
  const { rerouteUrl, forceReroute, isStartNewStation } = yield call(getEndPremiumAccessSearchData);
  if (rerouteUrl) {
    yield put(push(rerouteUrl));
    if (isStartNewStation) {
      return;
    }
  }
  const stationCount = yield select(getInDomain('playback.utils.getTotalStationCount'));
  const stationId = yield select(getInDomain('playback.utils.getLastPlayedStationId', null));
  log.info({ stationCount, stationId }, 'endPremiumAccessReroute');
  if (forceReroute) {
    if (stationCount > 0 && stationId) {
      log.info('endPremiumAccessReroute: redirect to StationPlayPath');
      yield put(push(RouteUtils.getStationPlayPathForStationCode(stationId)));
    } else {
      log.info('endPremiumAccessReroute: clear playlist and redirect to My Stations');
      yield put(AppActions.Playback.setPlaylist({ id: null }));
      yield put(push(RouteUtils.getMyStationsPath()));
    }
  }
  // The newly rendered T1/T2 search box is getting focus for some reason, so we have to remove it
  document.activeElement && document.activeElement.blur();
}

export function* endPremiumAccessStartStation() {
  const stationCount = yield select(getInDomain('playback.utils.getTotalStationCount'));
  const stationId = yield select(getInDomain('playback.utils.getLastPlayedStationId', null));
  if (stationCount > 0 && stationId) {
    log.info(`endPremiumAccessStartStation stationId: ${stationId}`);
    const { forceReroute } = yield call(getEndPremiumAccessSearchData);
    yield call(
      putAndWait,
      AppActions.UseCases.startStation({
        forceNextTrack: true,
        forceReroute,
        stationId,
        suppressAudioAds: true,
        suppressVideoAds: true,
      }),
    );
  }
}

export function* closeOverlay() {
  log.info('closeOverlay');
  yield put(
    AppActions.Overlay.closeOverlay({
      [AppConstants.Overlay.OVERLAY_CONFIG.TYPE]: AppConstants.Overlay.OVERLAY_TYPES.modal,
      [AppConstants.Overlay.OVERLAY_CONFIG.SUBTYPE]:
        AppConstants.Overlay.OVERLAY_TYPES.ModalCoachMark,
    }),
  );
}

export function* endPremiumAccessListenerEducation(hasExpiredBeforeSessionStart) {
  log.info('endPremiumAccessListenerEducation');
  const premiumAccessAudioData = yield select(
    getInDomain('listener.utils.getPremiumAccessLTUXEducationAudioData'),
    hasExpiredBeforeSessionStart,
  );
  const isAudioPlaying = yield select(getInDomain('playback.utils.isAudioPlaying', false));

  if (isAudioPlaying) {
    yield call(putAndWait, AppActions.UseCases.pauseTrack());
  }
  yield put(getInDomain('stations.actions.setShouldShortCircuitAfterGetFragment')(true));
  yield call(
    putAndWait,
    AppActions.SamplePlayer.playSample(
      premiumAccessAudioData.url,
      premiumAccessAudioData.resumeTrackOnEnd,
    ),
  );
  yield put(getInDomain('stations.actions.setShouldShortCircuitAfterGetFragment')(false));

  if (!hasExpiredBeforeSessionStart) {
    yield call(
      putAndWait,
      getInDomain('listener.actions.incrementPremiumAccessEducationExitMessageCount')(),
    );
  }
}

export function* endPremiumAccess(hasExpiredBeforeSessionStart, continueSource) {
  log.info('endPremiumAccess');
  const { PREMIUM_ACCESS } = AppConstants.Ads.Rewards.RewardTypes;

  yield put(getInDomain('rewards.actions.clearReward')(PREMIUM_ACCESS));
  yield put(AppActions.Application.refreshUIDAfterPASession());

  if (continueSource) {
    const { cancel } = yield race({
      trackEnd: take(AppConstants.AudioPlayer.AudioPlayerActions.ENDED),
      skip: take(AppConstants.UseCases.ActionTypes.SKIP_TRACK_FORWARD),
      cancel: take([
        AppConstants.Playback.ActionTypes.SET_OR_CREATE_STATION_FINISHED,
        AppConstants.Api.ApiActions.PLAYBACK_SOURCE_SUCCESS,
      ]),
    });

    if (cancel) {
      return;
    }
  }

  yield call(openUpsellModal);
  yield call(endPremiumAccessReroute);
  yield call(endPremiumAccessListenerEducation, hasExpiredBeforeSessionStart);

  yield call(endPremiumAccessStartStation);
  yield put(AppActions.Application.updateDominantColor(null));
  yield call(closeOverlay);
}
